/**
 * CanvasObjects.jsx
 * Componentes Konva compartidos entre ScadaEditor y ScadaViewer.
 */

import React from 'react';
import { Group, Rect, Circle, Text, Line, RegularPolygon } from 'react-konva';

// ─── Tipos de objetos del canvas ──────────────────────────────────────────────

export const OBJECT_CATEGORIES = [
  {
    label: 'Sensores',
    color: '#4d9fff',
    items: [
      { type: 'sensor_temp',     icon: '🌡', label: 'Temperatura', unit: '°C' },
      { type: 'sensor_pressure', icon: '⊙',  label: 'Presión',     unit: 'bar' },
      { type: 'sensor_current',  icon: '⚡', label: 'Corriente',   unit: 'A' },
      { type: 'sensor_digital',  icon: '◉',  label: 'Digital',     unit: '' },
    ],
  },
  {
    label: 'Actuadores',
    color: '#00d4aa',
    items: [
      { type: 'motor',   icon: '⚙',  label: 'Motor'   },
      { type: 'valve',   icon: '⧖',  label: 'Válvula' },
      { type: 'relay',   icon: '⊟',  label: 'Relé'    },
    ],
  },
  {
    label: 'Indicadores',
    color: '#f59e0b',
    items: [
      { type: 'indicator',       icon: '●',   label: 'Piloto'   },
      { type: 'alarm_light',     icon: '⚠',   label: 'Alarma'   },
      { type: 'level_bar',       icon: '▮',   label: 'Nivel'    },
      { type: 'numeric_display', icon: '0.0', label: 'Número'   },
    ],
  },
  {
    label: 'Controles',
    color: '#a78bfa',
    items: [
      { type: 'switch', icon: '⊡', label: 'Switch' },
      { type: 'button', icon: '▣', label: 'Botón'  },
    ],
  },
  {
    label: 'Planta',
    color: '#94a3b8',
    items: [
      { type: 'tank',  icon: '▬', label: 'Tanque'   },
      { type: 'pipe',  icon: '━', label: 'Tubería'  },
      { type: 'label', icon: 'T', label: 'Etiqueta' },
    ],
  },
];

export const TYPE_META = {};
OBJECT_CATEGORIES.forEach(cat =>
  cat.items.forEach(item => {
    TYPE_META[item.type] = { icon: item.icon, label: item.label, color: cat.color, unit: item.unit || '' };
  })
);

export const TYPE_PREFIX = {
  sensor_temp: 'tmp', sensor_pressure: 'pre', sensor_current: 'cur', sensor_digital: 'dig',
  motor: 'mot', valve: 'vlv', relay: 'rel',
  indicator: 'plt', alarm_light: 'alm', level_bar: 'lvl', numeric_display: 'num',
  switch: 'swt', button: 'btn', tank: 'tnk', pipe: 'pip', label: 'lbl',
};

// ─── Determina si un valor de tag representa "activo/encendido" ───────────────

export function isTagActive(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number')  return value !== 0;
  if (typeof value === 'string')  return value === '1' || value.toLowerCase() === 'true' || value.toLowerCase() === 'on';
  return false;
}

// ─── Renderizador de formas (Konva) ───────────────────────────────────────────

export function ObjectShape({ type, w, h, color }) {
  const cx = w / 2, cy = h / 2;
  const r  = Math.min(w, h) * 0.38;
  const shapeProps = { fill: color, opacity: 0.9, listening: false };
  const iconStyle  = { fontSize: Math.min(w, h) * 0.4, fill: '#fff', align: 'center', listening: false };

  switch (type) {
    case 'sensor_temp':
      return (<>
        <Rect x={cx - 6} y={4} width={12} height={h - 20} cornerRadius={6} {...shapeProps} />
        <Circle x={cx} y={h - 12} radius={10} {...shapeProps} />
        <Text x={0} y={cy - 6} width={w} text={TYPE_META[type].icon} {...iconStyle} />
      </>);
    case 'sensor_pressure':
      return (<>
        <Circle x={cx} y={cy} radius={r} {...shapeProps} />
        <Circle x={cx} y={cy} radius={r * 0.6} fill='#1e293b' opacity={0.8} listening={false} />
        <Line points={[cx, cy, cx + r * 0.5, cy - r * 0.5]} stroke='#fff' strokeWidth={2} listening={false} />
        <Text x={0} y={h - 18} width={w} text='P' fontSize={11} fill='#fff' align='center' listening={false} />
      </>);
    case 'sensor_current':
      return (<>
        <Circle x={cx} y={cy} radius={r} {...shapeProps} />
        <Text x={0} y={cy - 14} width={w} text='A' fontSize={Math.min(w,h)*0.35} fill='#fff' align='center' fontStyle='bold' listening={false} />
      </>);
    case 'sensor_digital':
      return (<>
        <Rect x={cx - r} y={cy - r} width={r*2} height={r*2} cornerRadius={4} {...shapeProps} />
        <Circle x={cx} y={cy} radius={r * 0.4} fill='#1e293b' opacity={0.8} listening={false} />
      </>);
    case 'motor':
      return (<>
        <Circle x={cx} y={cy} radius={r} {...shapeProps} />
        <Circle x={cx} y={cy} radius={r * 0.55} fill='#1e293b' opacity={0.8} listening={false} />
        {[0, 60, 120, 180, 240, 300].map(deg => {
          const rad = (deg * Math.PI) / 180;
          return <Line key={deg} points={[cx + r*0.55*Math.cos(rad), cy + r*0.55*Math.sin(rad), cx + r*0.9*Math.cos(rad), cy + r*0.9*Math.sin(rad)]} stroke={color} strokeWidth={3} listening={false} />;
        })}
        <Text x={0} y={cy-8} width={w} text='M' fontSize={14} fill='#fff' align='center' fontStyle='bold' listening={false} />
      </>);
    case 'valve':
      return (<>
        <RegularPolygon x={cx} y={cy} sides={3} radius={r} rotation={0} {...shapeProps} />
        <RegularPolygon x={cx} y={cy} sides={3} radius={r} rotation={180} fill={color} opacity={0.7} listening={false} />
        <Rect x={cx-3} y={4} width={6} height={12} fill={color} opacity={0.9} listening={false} />
      </>);
    case 'relay':
      return (<>
        <Rect x={cx-r} y={cy-r*0.7} width={r*2} height={r*1.4} cornerRadius={3} {...shapeProps} />
        <Line points={[cx-r*0.4, cy, cx+r*0.1, cy-r*0.4, cx+r*0.4, cy]} stroke='#fff' strokeWidth={2} listening={false} />
        <Circle x={cx+r*0.4} y={cy} radius={3} fill='#fff' listening={false} />
      </>);
    case 'indicator':
      return (<>
        <Circle x={cx} y={cy} radius={r} {...shapeProps} />
        <Circle x={cx} y={cy} radius={r*0.6} fill='#fff' opacity={0.15} listening={false} />
      </>);
    case 'alarm_light':
      return (<>
        <Circle x={cx} y={cy} radius={r} fill='#ef4444' opacity={0.85} listening={false} />
        <Text x={0} y={cy-14} width={w} text='!' fontSize={22} fill='#fff' align='center' fontStyle='bold' listening={false} />
      </>);
    case 'level_bar':
      return (<>
        <Rect x={cx-14} y={4} width={28} height={h-8} cornerRadius={3} stroke={color} strokeWidth={2} fill='#1e293b' listening={false} />
        <Rect x={cx-12} y={h/2} width={24} height={h/2-6} fill={color} opacity={0.85} listening={false} />
        <Text x={0} y={2} width={w} text='50%' fontSize={10} fill='#94a3b8' align='center' listening={false} />
      </>);
    case 'numeric_display':
      return (<>
        <Rect x={4} y={cy-16} width={w-8} height={32} cornerRadius={4} fill='#0f172a' stroke={color} strokeWidth={1.5} listening={false} />
        <Text x={4} y={cy-10} width={w-8} text='0.00' fontSize={18} fill={color} align='center' fontStyle='bold' listening={false} />
      </>);
    case 'switch':
      return (<>
        <Rect x={cx-22} y={cy-12} width={44} height={24} cornerRadius={12} fill='#374151' listening={false} />
        <Circle x={cx-10} y={cy} radius={10} fill='#6b7280' listening={false} />
      </>);
    case 'button':
      return (<>
        <Rect x={cx-r} y={cy-r*0.6} width={r*2} height={r*1.2} cornerRadius={6} {...shapeProps} />
        <Text x={0} y={cy-8} width={w} text='BTN' fontSize={12} fill='#fff' align='center' listening={false} />
      </>);
    case 'tank':
      return (<>
        <Rect x={8} y={8} width={w-16} height={h-16} cornerRadius={6} stroke={color} strokeWidth={2} fill='#1e293b' listening={false} />
        <Rect x={10} y={h*0.5} width={w-20} height={h*0.44} fill={color} opacity={0.3} listening={false} />
      </>);
    case 'pipe':
      return <Rect x={0} y={h/2-6} width={w} height={12} cornerRadius={6} {...shapeProps} />;
    case 'label':
      return <Text x={2} y={cy-10} width={w-4} text='Label' fontSize={14} fill='#e2e8f0' align='center' listening={false} />;
    default:
      return <Rect x={cx-r} y={cy-r} width={r*2} height={r*2} {...shapeProps} />;
  }
}

// ─── Objeto del canvas (Konva Group) ─────────────────────────────────────────

export function CanvasObject({ obj, selected, onSelect, onDragEnd, tagValue, isRuntime, onRuntimeClick }) {
  const baseMeta = TYPE_META[obj.type];
  const active   = isRuntime ? isTagActive(tagValue) : false;

  const color = isRuntime
    ? (active ? (obj.style?.colorOn || '#00d4aa') : (obj.style?.colorOff || '#4b5563'))
    : (baseMeta?.color || '#4d9fff');

  const inAlarm = isRuntime && obj.alarm?.enabled && typeof tagValue === 'number' && (
    (obj.alarm.min !== null && tagValue < obj.alarm.min) ||
    (obj.alarm.max !== null && tagValue > obj.alarm.max)
  );
  const effectiveColor = inAlarm ? (obj.style?.colorAlarm || '#ef4444') : color;

  const handleClick = isRuntime && onRuntimeClick
    ? () => onRuntimeClick(obj.id)
    : (onSelect ? () => onSelect(obj.id) : undefined);

  return (
    <Group
      id={obj.id}
      x={obj.x}
      y={obj.y}
      width={obj.w}
      height={obj.h}
      draggable={!!onDragEnd}
      onClick={handleClick}
      onTap={handleClick}
      onDragEnd={onDragEnd ? e => onDragEnd(obj.id, { x: e.target.x(), y: e.target.y() }) : undefined}
    >
      <Rect
        width={obj.w}
        height={obj.h}
        cornerRadius={6}
        fill={isRuntime ? (active ? '#0a2a1a' : '#1e293b') : '#1e293b'}
        stroke={inAlarm ? '#ef4444' : selected ? '#3b82f6' : (isRuntime && active ? effectiveColor : '#374151')}
        strokeWidth={inAlarm ? 2 : selected ? 2 : (isRuntime && active ? 1.5 : 1)}
        shadowColor={inAlarm ? '#ef4444' : selected ? '#3b82f6' : (isRuntime && active ? effectiveColor : 'transparent')}
        shadowBlur={inAlarm ? 12 : selected ? 8 : (isRuntime && active ? 6 : 0)}
        listening={true}
      />
      <ObjectShape type={obj.type} w={obj.w} h={obj.h} color={effectiveColor} value={tagValue} />
      <Text
        x={0} y={obj.h - 16} width={obj.w}
        text={obj.label} fontSize={10}
        fill={isRuntime && active ? '#e2e8f0' : '#94a3b8'}
        align='center' listening={false}
      />
      {isRuntime && tagValue !== undefined && tagValue !== null && (
        <Text
          x={0} y={4} width={obj.w}
          text={typeof tagValue === 'number' ? tagValue.toFixed(2) : String(tagValue)}
          fontSize={9} fill={effectiveColor} align='center' listening={false}
        />
      )}
      {!isRuntime && obj.tag && (
        <Rect x={1} y={1} width={8} height={8} cornerRadius={2} fill='#22c55e' listening={false} />
      )}
      {selected && !isRuntime && [
        [0, 0], [obj.w, 0], [0, obj.h], [obj.w, obj.h]
      ].map(([hx, hy], i) => (
        <Circle key={i} x={hx} y={hy} radius={4} fill='#3b82f6' listening={false} />
      ))}
    </Group>
  );
}
